import { Badge, Tooltip } from '@provenance/design';
import { type IntakeChannel } from '@provenance/shared';
import { Mail, Plug, Upload } from 'lucide-react';

/**
 * How a document arrived — spec v4 §23.
 *
 * This is not decoration. The assurance behind a document differs sharply by channel: an
 * integrator submission is authenticated by a hashed API key and carries a full asserted
 * payload, while an emailed PDF arrives from a header anyone can forge and is confirmed
 * only by a round-trip to a registered address. An operator deciding how hard to look
 * needs to know which one they are holding, on every screen, without having to work it out.
 *
 * The icon is never the only carrier of that meaning — the channel is in the accessible
 * name too, because an icon alone is invisible to a screen reader and ambiguous to
 * everyone else.
 */
const CHANNEL: Record<
  IntakeChannel,
  { label: string; icon: typeof Plug; tone: 'neutral' | 'info'; hint: string }
> = {
  INTEGRATOR: {
    label: 'Integrator',
    icon: Plug,
    tone: 'neutral',
    hint: 'Lodged through the integration API, authenticated by an API key, with the full submitted data.',
  },
  UPLOAD: {
    label: 'Upload',
    icon: Upload,
    tone: 'info',
    hint: 'Uploaded by a registered signatory who confirmed their email address. No submitted data — the details were read from the certificate.',
  },
  EMAIL: {
    label: 'Email',
    icon: Mail,
    tone: 'info',
    hint: 'Arrived by email. No submitted data — the details were read from the certificate.',
  },
};

export function IntakeChannelBadge({
  channel,
  identity,
  size = 'sm',
}: {
  channel: IntakeChannel;
  identity?: string | null;
  size?: 'sm' | 'md';
}) {
  const c = CHANNEL[channel] ?? CHANNEL.INTEGRATOR;
  const Icon = c.icon;
  return (
    <Tooltip content={c.hint}>
      <span className="inline-flex">
        <Badge tone={c.tone} size={size}>
          <Icon aria-hidden className="size-2.5" />
          <span>{identity?.trim() || c.label}</span>
          {/* The channel name for assistive tech, where the identity alone ("SageDocs")
              would not say how the document got here. */}
          <span className="sr-only"> — arrived by {c.label.toLowerCase()}</span>
        </Badge>
      </span>
    </Tooltip>
  );
}
